import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Event } from '../types/models';
import { formatTime } from '../utils/dateUtils';
import { motion } from 'framer-motion';
import LocationDisplay from './LocationDisplay';
import { FiCalendar, FiMapPin, FiUsers, FiImage } from 'react-icons/fi';
import { CalendarIcon, MapPinIcon, ClockIcon, TagIcon, UsersIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { Badge } from './ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { cn } from '../lib/utils';
import { getEventImages } from '../utils/getEventImages';
import { getTicketTiersArray, getMinTicketPrice } from '../utils/eventHelpers';
import { Calendar, Clock } from 'lucide-react';

interface EventCardProps {
  event: Event;
  index?: number;
}

export const EventCard: React.FC<EventCardProps> = ({ event, index = 0 }) => {
  // Add check for null event to prevent rendering issues
  if (!event) {
    return (
      <Card className="h-full flex flex-col overflow-hidden">
        <CardContent className="flex items-center justify-center h-40">
          <div className="text-center text-gray-400">
            <FiImage className="h-10 w-10 mx-auto mb-2" />
            <p>Event not available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const DEFAULT_EVENT_IMAGE = '/images/event-placeholder.jpg';
  const [imageLoaded, setImageLoaded] = useState(false);

  // Robustly select the best image
  const getBestImage = (event: Event): string => {
    if (event.coverImage && typeof event.coverImage === 'string' && event.coverImage.trim()) return event.coverImage;
    if (event.imageUrl && typeof event.imageUrl === 'string' && event.imageUrl.trim()) return event.imageUrl;
    if (event.media?.images?.[0] && typeof event.media.images[0] === 'string' && event.media.images[0].trim()) return event.media.images[0];
    return DEFAULT_EVENT_IMAGE;
  };

  // Load image on component mount
  useEffect(() => {
    const bestImage = getBestImage(event);
    setImageUrl(bestImage);
  }, [event]);

  const formatStatus = (status?: string) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-primary/10 text-primary';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventImage = async (): Promise<string> => {
    try {
      // Collect all possible image sources
      const possibleImages: string[] = [];
      
      // Check for images in various locations
      if (event.imageUrl && typeof event.imageUrl === 'string' && event.imageUrl.trim()) {
        possibleImages.push(event.imageUrl);
      }
      
      if ((event as any).coverImage && typeof (event as any).coverImage === 'string' && (event as any).coverImage.trim()) {
        possibleImages.push((event as any).coverImage);
      }
      
      // Check media.images array safely
      if (event.media?.images && Array.isArray(event.media.images)) {
        event.media.images.forEach(url => {
          if (typeof url === 'string' && url.trim()) {
            possibleImages.push(url);
          }
        });
      }
      
      // Use each image with proper URL formatting
      for (const img of possibleImages) {
        if (img.startsWith('http') || img.startsWith('https')) {
          return img;
        }
        
        // Handle relative paths
        if (img.startsWith('/')) {
          return `${window.location.origin}${img}`;
        }
      }
      
      // If no valid images found, use a placeholder in the public folder
      return '/images/event-placeholder.jpg';
    } catch (error) {
      console.error('Error getting event image:', error);
      return '/images/event-placeholder.jpg';
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Safely format date from Timestamp
  const safeFormatDate = (dateTime: any) => {
    if (!dateTime) return 'Date not set';
    try {
      // Handle Firestore Timestamp
      if (dateTime.toDate && typeof dateTime.toDate === 'function') {
        const jsDate = dateTime.toDate();
        return format(jsDate, 'MMM d, yyyy');
      }
      // Handle Date object or string
      return format(new Date(dateTime), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Safely get location string
  const getLocationString = (event: any) => {
    try {
      if (!event) return 'Location not set';
      
      if (event.address) return event.address;
      if (event.location?.address) return event.location.address;
      if (event.location?.city && event.location?.state) return `${event.location.city}, ${event.location.state}`;
      if (event.location?.city) return event.location.city;
      return 'Location not set';
    } catch (error) {
      return 'Location not set';
    }
  };

  // Safely get available tickets
  const getAvailableTickets = (event: any): number => {
    try {
      return getTicketTiersArray(event).reduce(
        (sum: number, tier: any) => sum + ((tier.quantity || 0) - (tier.sold || 0)), 
        0
      );
    } catch (error) {
      return 0;
    }
  };

  // Safely get min price
  const getMinPrice = (event: any): string => {
    try {
      const minPrice = getMinTicketPrice(event);
      if (
        minPrice === null ||
        minPrice === undefined ||
        minPrice === 0 ||
        !isFinite(minPrice) ||
        isNaN(minPrice)
      ) {
        return 'Free';
      }
      return `$${minPrice.toFixed(2)}`;
    } catch (error) {
      return 'Free';
    }
  };

  // Render the event card with careful error handling
  try {
    const missingInfo = !event.title || !imageUrl || imageUrl === DEFAULT_EVENT_IMAGE;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 border border-slate-100 cursor-pointer group"
        whileHover={{ scale: 1.025 }}
        onClick={() => window.location.assign(`/events/${event.id}`)}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
          <img
            src={imageUrl}
            alt={event.title || 'Event image'}
            className={`w-full h-full object-cover rounded-t-2xl transition-transform duration-300 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            onError={handleImageError}
            onLoad={() => setImageLoaded(true)}
          />
          {/* Date and category/status pills */}
          <div className="absolute top-4 left-4 flex gap-2 z-10">
            <span className="bg-primary text-white rounded-full px-3 py-1 text-xs font-semibold shadow">
              {safeFormatDate(event.startDateTime)}
            </span>
            {event.category && (
              <span className="bg-white/80 text-gray-700 rounded-full px-3 py-1 text-xs font-medium shadow">
                {event.category}
              </span>
            )}
            {event.status && (
              <span className="bg-white/80 text-gray-700 rounded-full px-3 py-1 text-xs font-medium shadow">
                {formatStatus(event.status)}
              </span>
            )}
          </div>
        </div>
        <div className="p-5 flex flex-col gap-2">
          <h3 className="text-xl font-extrabold text-gray-900 mb-1 line-clamp-2">
            {event.title || 'Untitled Event'}
          </h3>
          {event.description && (
            <p className="text-gray-600 text-sm mb-2 line-clamp-2">
              {event.description.length > 100
                ? event.description.slice(0, 100) + '...'
                : event.description}
            </p>
          )}
          <div className="flex flex-wrap items-center text-sm text-gray-600 gap-x-6 gap-y-2 mb-1">
            <span className="flex items-center gap-1">
              <FiMapPin className="w-4 h-4" />
              {getLocationString(event)}
            </span>
            {event.ticketTiers && event.ticketTiers.length > 0 && (
              <span className="flex items-center gap-1 text-primary font-semibold">
                <BanknotesIcon className="w-4 h-4" />
                {getMinPrice(event)}
              </span>
            )}
            {event.ticketTiers && event.ticketTiers.length > 0 && (
              <span className="flex items-center gap-1 text-primary font-semibold">
                <UsersIcon className="w-4 h-4" />
                {getAvailableTickets(event)} tickets left
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {/* ... existing code ... */}
          </div>
        </div>
      </motion.div>
    );
  } catch (error) {
    console.error('Error rendering event card:', error);
    return (
      <Card className="h-full flex flex-col overflow-hidden">
        <CardContent className="flex items-center justify-center h-40">
          <div className="text-center text-red-400">
            <FiImage className="h-10 w-10 mx-auto mb-2" />
            <p>Error displaying event</p>
          </div>
        </CardContent>
      </Card>
    );
  }
}; 